<template>
    <div v-if="hasTemplate && !nonStandardLicensePlate" :class="{ 'is-invalid pr-3': isInvalid }" class="license-plate-input-container">
        <input :name="name" :value="licensePlate" type="hidden" />

        <div :style="licensePlateTemplateStyling" class="license-plate-input-form-group position-relative clearfix">
            <div class="license-plate-input">
                <div :style="field1ContainerStyle" class="license-plate-input-field">
                    <div v-if="disabled" class="field-preview form-control h-100">
                        <div>{{ templateInput.field1 }}</div>
                    </div>
                    <input
                        v-else
                        :id="id + 'portion1'"
                        v-model="templateInput.field1"
                        :maxlength="template.field_1_maxlength"
                        :name="name + 'portion1'"
                        :placeholder="shouldHidePlaceholders ? '' : template.field_1_placeholder"
                        autocomplete="off"
                        class="form-control h-100"
                        type="text"
                        @keyup="setPlaceholderVisibility"
                    />
                </div>
                <div :style="field2ContainerStyle" class="license-plate-input-field">
                    <div v-if="disabled" class="field-preview form-control h-100">
                        <div>{{ templateInput.field2 }}</div>
                    </div>
                    <input
                        v-else
                        :id="id + 'portion2'"
                        v-model="templateInput.field2"
                        :maxlength="template.field_2_maxlength"
                        :name="name + 'portion2'"
                        :placeholder="shouldHidePlaceholders ? '' : template.field_2_placeholder"
                        autocomplete="off"
                        class="form-control h-100"
                        type="text"
                        @keyup="setPlaceholderVisibility"
                    />
                </div>
                <div v-if="hasThirdField" :style="field3ContainerStyle" class="license-plate-input-field">
                    <div v-if="disabled" class="field-preview form-control h-100">
                        <div>{{ templateInput.field3 }}</div>
                    </div>
                    <input
                        v-else
                        :id="id + 'portion3'"
                        v-model="templateInput.field3"
                        :maxlength="template.field_3_maxlength"
                        :name="name + 'portion3'"
                        :placeholder="shouldHidePlaceholders ? '' : template.field_3_placeholder"
                        autocomplete="off"
                        class="form-control h-100"
                        type="text"
                        @keyup="setPlaceholderVisibility"
                    />
                </div>
            </div>
            <img :src="licensePlateCustomBackground" alt="license-plate" />
            <div :style="{ left: licensePlateContainerWidth + '%' }" class="invalid-icon"></div>
        </div>

        <div ref="scrollTarget" :class="{ 'd-block': isInvalid }" class="invalid-feedback">
            <slot></slot>
        </div>

        <div v-if="this.showSpecialCharactersInfo && !this.name.includes('confirmation')" class="mt-2">
            {{ $t('shop.umlautWarning') }}
        </div>

        <div v-if="validationData.showSpecialCheckbox" class="mt-2">
            <input name="is_special_license_plate" type="hidden" value="1" />

            <div class="custom-control custom-checkbox">
                <input
                    id="license-is-special"
                    v-model="validationData.isSpecial"
                    :value="true"
                    class="custom-control-input"
                    name="special_license_plate_confirmed"
                    required
                    type="checkbox"
                />
                <label
                    class="custom-control-label"
                    for="license-is-special"
                    v-html="$t('shop.isSpecialLicensePlate')"
                ></label>
            </div>

            <div
                v-if="
                    typeof specialLicensePlateError === 'object' &&
                    specialLicensePlateError instanceof Array &&
                    specialLicensePlateError.length > 0
                "
                class="invalid-feedback d-block"
            >
                <span
                    v-for="message in specialLicensePlateError"
                    :key="message"
                    class="d-block"
                    v-html="message"
                ></span>
            </div>
        </div>
    </div>
    <div v-else>
        <div class="license-plate-input-form-group">
            <div class="input-group">
                <div class="input-group-prepend">
                    <div :style="licensePlateWrapper" class="input-group-text">
                        <div :style="licensePlateBackground" class="license-plate-background"></div>
                    </div>
                </div>
                <input
                    :id="id"
                    v-model="licensePlate"
                    :class="{
                        'is-invalid': isInvalid,
                    }"
                    :disabled="disabled"
                    :name="name"
                    :placeholder="licensePlateCountry.placeholder"
                    :required="required"
                    class="form-control height-auto-override"
                    type="text"
                />
                <div class="invalid-feedback">
                    <slot></slot>
                </div>
            </div>
        </div>

        <div v-if="validationData.showSpecialCheckbox" class="mt-2">
            <input name="is_special_license_plate" type="hidden" value="1" />

            <div class="custom-control custom-checkbox">
                <input
                    id="license-is-special-default"
                    v-model="validationData.isSpecial"
                    :value="true"
                    class="custom-control-input"
                    name="special_license_plate_confirmed"
                    required
                    type="checkbox"
                />
                <label
                    class="custom-control-label"
                    for="license-is-special-default"
                    v-html="$t('shop.isSpecialLicensePlate')"
                ></label>
            </div>

            <div
                v-if="
                    typeof specialLicensePlateError === 'object' &&
                    specialLicensePlateError instanceof Array &&
                    specialLicensePlateError.length > 0
                "
                class="invalid-feedback d-block"
            >
                <span
                    v-for="message in specialLicensePlateError"
                    :key="message"
                    class="d-block"
                    v-html="message"
                ></span>
            </div>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
    props: {
        required: {
            type: Boolean,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        id: {
            type: String,
            required: true,
        },
        licensePlateCountry: {
            type: Object,
            required: true,
        },
        isInvalid: {
            type: Boolean,
            required: true,
        },
        hidePlaceholders: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        value: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        template: {
            type: Object,
            required: false,
            default() {
                return {
                    id: null,
                    background: null,
                    template: '%field_1%-%field_2%%field_3%',
                    regex: '^([A-ZÖÜÄ]{1,3})-([A-ZÖÜÄ]{1,2})([0-9]{1,5})',
                    padding_top: null,
                    padding_left: null,
                    field_1_width: null,
                    field_1_margin_right: null,
                    field_1_placeholder: null,
                    field_1_maxlength: null,
                    field_2_width: null,
                    field_2_margin_right: null,
                    field_2_placeholder: null,
                    field_2_maxlength: null,
                    field_3_width: null,
                    field_3_margin_right: null,
                    field_3_placeholder: null,
                    field_3_maxlength: null,
                };
            },
        },

        update: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },

        disableValidation: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },

        specialLicensePlateError: {
            type: Array,
            required: false,
            default() {
                return null;
            },
        },
        disabled: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        nonStandardLicensePlate: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
    },

    data() {
        return {
            showSpecialCharactersInfo: false,
            licensePlate: this.value,
            licensePlateContainerWidth: 100,
            templateMatching: true,
            templateInput: {
                field1: '',
                field2: '',
                field3: '',
            },
            validationData: {
                disabled: this.disableValidation,
                isOngoing: false,
                isValid: false,
                showSpecialCheckbox: false,
                isSpecial: false,
                xhr: null,
            },
            shouldHidePlaceholders: false,
        };
    },

    computed: {
        /**
         * Determine if the license plate country has a special template for input.
         *
         * @returns {boolean}
         */
        hasTemplate() {
            return this.template !== null && this.template.id !== null && this.templateMatching;
        },

        /**
         * Determine if the license plate has a third input field.
         *
         * @returns {boolean}
         */
        hasThirdField() {
            return this.hasTemplate && this.template.field_3_width !== null;
        },

        /**
         * Generate the style attributes for legacy license plate input.
         *
         * @returns {{"background-image": string}|null}
         */
        licensePlateCustomBackground() {
            // In the case we don't have a license plate country, we're not able to search the
            // available image to display at the input group.
            if (this.licensePlateCountry === null) {
                return null;
            }

            // If the license plate country allows the usage of the input template, we have to
            // use this image instead of the country defined one. But only if the template has
            // a usable image.
            if (
                typeof this.licensePlateCountry.use_template_image === 'boolean' &&
                this.licensePlateCountry.use_template_image &&
                this.hasTemplate &&
                typeof this.template.background === 'string' &&
                this.template.background.trim().length > 0
            ) {
                return this.getPrefixForImageLink(this.template.background) + this.template.background;
            }

            // As fallback, we have to check the country definition. If the country does not
            // have a image to use, we have to prevent the display of a broken image.
            if (typeof this.licensePlateCountry.image !== 'string' || !this.licensePlateCountry.image.length) {
                return null;
            }

            return this.getPrefixForImageLink(this.licensePlateCountry.image) + this.licensePlateCountry.image;
        },

        /**
         * Generate the style attributes for legacy license plate input.
         *
         * @returns {{"background-image": string}|null}
         */
        licensePlateBackground() {
            if (
                this.licensePlateCountry === null ||
                typeof this.licensePlateCountry.image !== 'string' ||
                !this.licensePlateCountry.image.length
            ) {
                return null;
            }

            // Default prefix to apply for each background image
            const prefix = this.getPrefixForImageLink(this.licensePlateCountry.image);
            const styles = {
                'background-image': `url('${prefix}${this.licensePlateCountry.image}')`,
            };

            if (this.licensePlateCountry.image.indexOf('/generic/') !== -1) {
                styles['background-size'] = 'contain';
                styles['background-position'] = 'center';
                styles.height = '36px';
                styles.width = '20px';
            }

            return styles;
        },

        /**
         * Generate the style attributes for legacy license plate input.
         *
         * @returns {{"padding": string}|null}
         */
        licensePlateWrapper() {
            if (
                this.licensePlateCountry === null ||
                typeof this.licensePlateCountry.image !== 'string' ||
                !this.licensePlateCountry.image.length
            ) {
                return null;
            }

            if (this.licensePlateCountry.image.includes('/generic/')) {
                return {
                    padding: '0',
                    'border-radius': '0',
                };
            }

            return null;
        },

        /**
         * Generate the style attribute content for license plate input container.
         *
         * @returns {null|{"padding-top": string, "padding-left": string, "background-image": string, "width": string}}
         */
        licensePlateTemplateStyling() {
            // In the case we don't have an template for this country.
            if (!this.hasTemplate) {
                return null;
            }

            return {
                //'background-image': 'url(\'/' + this.template.background + '\')',
                //'padding-top': this.template.padding_top + '%',
                //'padding-left': this.template.padding_left + '%',
                width: this.licensePlateContainerWidth + '%',
                position: 'relative',
            };
        },

        /**
         * Generate the styling of first input field.
         *
         * @returns {null|{"width": string, "margin-right": string}}
         */
        field1ContainerStyle() {
            if (!this.hasTemplate) {
                return null;
            }

            return this.getFieldContainerStyle(1);
        },

        /**
         * Generate the styling of second input field.
         *
         * @returns {null|{"width": string, "margin-right": string}}
         */
        field2ContainerStyle() {
            if (!this.hasTemplate) {
                return null;
            }

            return this.getFieldContainerStyle(2);
        },

        /**
         * Generate the styling of third input field.
         *
         * @returns {null|{"width": string, "margin-right": string}}
         */
        field3ContainerStyle() {
            if (!this.hasThirdField) {
                return null;
            }

            return this.getFieldContainerStyle(3);
        },
    },

    watch: {
        /**
         * Watches the update flag, to handle the update the license plate input fields.
         *
         * @returns {void}
         * */
        update() {
            if (!this.update) {
                return;
            }

            this.licensePlate = this.value;
            this.convertValueIntoTemplateInput();

            this.$emit('model-updated');
        },

        /**
         * Watches the template module and emit an event to update the form view.
         *
         * @return {void}
         * */
        template() {
            this.$emit('has-license-plate-template', this.hasTemplate);
        },

        /**
         * Watch the license plate input.
         *
         * @return {void}
         */
        licensePlate() {
            this.licensePlate = this.transformInput(this.licensePlate, false);
            this.$emit('update:modelValue', this.licensePlate);

            // Send the validation to assure, we have a valid license plate and
            // maybe need to show the special license plate checkbox. To prevent
            // any unexpected order for license plates, which have a very specific usage.
            this.validate();
        },

        /**
         * Watch the template input fields, to convert the sections into one string.
         */
        templateInput: {
            deep: true,
            handler() {
                this.templateInput.field1 = this.transformInput(this.templateInput.field1, true);
                this.templateInput.field2 = this.transformInput(this.templateInput.field2, true);
                this.templateInput.field3 = this.transformInput(this.templateInput.field3, true);

                this.licensePlate = this.convertTemplateInput();
            },
        },

        /**
         * Watch the status of validation, if a validation is ongoing the form will be disabled.
         *
         * @returns {void}
         * */
        'validationData.isOngoing'() {
            const form = this.$el.closest('form');

            if (form === null) {
                return;
            }

            form.disabled = this.validationData.isOngoing;

            for (const button of form.querySelectorAll('[type="submit"], [type="reset"]')) {
                button.disabled = this.validationData.isOngoing;
            }
        },
        'validationData.isSpecial'(value) {
            this.$emit('is-special-changed', value);
        },
    },

    methods: {
        setPlaceholderVisibility() {
            this.shouldHidePlaceholders =
                this.templateInput.field1 !== '' ||
                this.templateInput.field2 !== '' ||
                this.templateInput.field3 !== '';
        },

        /**
         * Handle the transform of input.
         *
         * @param {string} value
         * @param {boolean} separatedFields
         *
         * @returns {string}
         * */
        transformInput(value, separatedFields) {
            // In the first step we should remove any whitespace character from the inputs
            value = value.replace(/\s+/g, '');

            // In the case we have an separated field, we should remove the -
            // separators.
            if (separatedFields) {
                value = value.replace(/-+/g, '');
            }

            // Check if the license plate got any special characters and replace them
            // if (value.match(/[Ä|Ö|Ü]/g)) {
            //     // Mark the special characters flag to display the user warning
            //     this.showSpecialCharactersInfo = true;
            //
            //     return this.replaceSpecialCharacters(value.toUpperCase());
            // }

            return value.toUpperCase();
        },

        /**
         * Replace the special characters with an ascii-umlaut-mapping.
         *
         * @param value
         * @returns {*}
         */
        replaceSpecialCharacters(value) {
            const umlautMap = {
                '\u00dc': 'U',
                '\u00c4': 'A',
                '\u00d6': 'O',
            };

            return value
                .replace(/[Ä|Ö|Ü]/g, (a) => {
                    const big = umlautMap[a.slice(0, 1)];
                    return big.charAt(0) + big.charAt(1).toLowerCase() + a.slice(1);
                })
                .replace(new RegExp('[' + Object.keys(umlautMap).join('|') + ']', 'g'), (a) => umlautMap[a]);
        },

        /**
         * Handle the style attribute generation of an specified field.
         *
         * @param field
         *
         * @returns {{"width": string, "margin-right": string}}
         */
        getFieldContainerStyle(field) {
            return {
                top: this.template['padding_top'] + '%',
                right: this.template['field_' + field + '_margin_right'] - this.template['padding_left'] + '%',
                width: this.template['field_' + field + '_width'] + '%',
                position: 'absolute',
            };
        },

        /**
         * Handle the convert of template input, into an glued license plate.
         *
         * @returns {string}
         */
        convertTemplateInput() {
            return ('' + this.template.template)
                .replace(/%field_1%/g, this.templateInput.field1)
                .replace(/%field_2%/g, this.templateInput.field2)
                .replace(/%field_3%/g, this.hasThirdField ? this.templateInput.field3 : '');
        },

        /**
         * Handle the convert of provided value into the template input, in the case it's defined.
         *
         * @returns {this}
         */
        convertValueIntoTemplateInput() {
            if (
                !this.hasTemplate ||
                !this.licensePlate.length ||
                typeof this.template.regex !== 'string' ||
                !this.template.regex.length
            ) {
                return this;
            }

            // Generate a regular expression by the provided regex string
            const regex = new RegExp(this.template.regex, 'gi'),
                matches = regex.exec(this.licensePlate);

            // In the case that we don't have a result, we're not able to
            // parse the results into the separated fields.
            if (!matches || !matches.length) {
                this.templateMatching = false;
                return this;
            }

            this.templateMatching = true;

            // Step the matches and set them into the separated fields.
            for (let i = 1; i < matches.length; i++) {
                // The field is not known, we're not able to push the value
                // to this field.
                if (typeof this.templateInput['field' + i] === 'undefined') {
                    continue;
                }

                this.templateInput['field' + i] = matches[i];
            }

            return this;
        },

        /**
         * Handle the pre-validation of license plate, to assure customer input is valid before
         * the order will be submitted. Otherwise, the customer may order a vignette or route toll for
         * a special license plate and does not know that's not valid for his own.
         *
         * @returns {void}
         * */
        async validate() {
            // In the case the pre validation is disabled at the including component,
            // we don't send any request to our server to handle the validation.
            if (this.validationData.disabled) {
                this.validationData.isOngoing = false;
                this.validationData.isValid = true;
                this.validationData.showSpecialCheckbox = false;
                this.validationData.isSpecial = false;

                return;
            }

            // Skip validation for non-standard license plates
            if (this.nonStandardLicensePlate) return;

            this.validationData.xhr();
        },

        /**
         * Determine the prefix for image, to include it as a link.
         *
         * @param {string} image
         *
         * @returns {string}
         * */
        getPrefixForImageLink(image) {
            // In the case the image starts with a slash, double slash or with a protocol prefix,
            // we don't have to add a prefix to this definition. Otherwise, we have to prepend
            // a single slash to the link, in order to assure the image can be loaded correctly.
            if (
                image.indexOf('/') === 0 ||
                image.indexOf('//') === 0 ||
                (image.match(/^[a-z]+:\/\//) || []).length > 0
            ) {
                return '';
            }

            return '/';
        },
    },

    /**
     * Handle the boot of module.
     *
     * @return {void}
     */
    mounted() {
        this.shouldHidePlaceholders = this.hidePlaceholders;

        this.convertValueIntoTemplateInput();
        this.$emit('has-license-plate-template', this.hasTemplate);

        // Define the debounced validation request, to always check the latest input.
        this.validationData.xhr = debounce(() => {
            // Skip validation for non-standard license plates
            if (this.nonStandardLicensePlate) return;

            // Update the flag to indicate that a verification request is ongoing.
            this.validationData.isOngoing = true;

            // In the case we don't have a value for license plate an validation request does not
            // make much sense. Because the route does not will match and a validation of empty data
            // does not improve the service.
            if (typeof this.licensePlate !== 'string' || this.licensePlate.trim().length === 0) {
                this.validationData.isOngoing = false;
                this.validationData.showSpecialCheckbox = false;
                this.validationData.isValid = false;
                this.validationData.isSpecial = false;

                return null;
            }

            return axios
                .get(
                    `/${this.$language}/backend/validate/license-plate/${this.licensePlate}/${this.licensePlateCountry.country_code}`
                )
                .then((response) => {
                    this.validationData.isValid = response.data.valid;
                    this.validationData.showSpecialCheckbox = response.data.isSpecial;
                    this.validationData.isOngoing = false;

                    if(!response.data.isSpecial) {
                        this.validationData.isSpecial = false;
                    }

                    this.$emit('license-plate-check-status', response.data.valid);
                })
                .catch(() => {
                    this.validationData.isOngoing = false;
                });
        }, 200);
    },
};
</script>

<style lang="scss">
.form.contains-license-plate-template [name='country_code'] {
    height: 50px;
}

.license-plate-input-container {
    position: relative;

    .license-plate-input {
        background-size: 250px;
        background-repeat: no-repeat;

        .license-plate-input-field {
            float: left;
            height: 70%;

            .form-control {
                font-size: clamp(1rem, 6vw, 2.6rem);
                line-height: 37px;
                padding: 2px;
                text-align: center;
            }
        }
    }

    .invalid-icon {
        display: none;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);

        width: 20px;
        height: 20px;
        margin-left: 5px;

        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
    }

    &.is-invalid .invalid-icon {
        display: block;
    }

    .field-preview > div {
        position: relative;
        top: 50%;
        transform: translateY(-50%);
    }
}
</style>
